// ─────────────────────────────────────────────────────────────────────────────
// BerBul — Follower / myTrades
// Mock Data Module  |  src/follower/mock/mockData.js
//
// All shapes confirmed against backend (server_v4.py) — April 2026
// Use this file for Phase 1 UI development. Replace with real API calls in
// Phase 2 (Lambda) and Phase 4 (EC2 socket).
// ─────────────────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────────────────
// PREFS  —  mirrors GET /follower/prefs response
// Editable via PUT /follower/prefs: max_contracts, max_daily_loss, max_daily_profit only
// PUT response is { status: "OK" } — merge sent payload into local state directly
// ─────────────────────────────────────────────────────────────────────────────
export const mockPrefs = {
    broker:            "alpaca",   // always lowercase
    trading_mode:      "paper",    // "paper" | "live"
    is_active:         true,
    is_paused:         false,
    pause_reason:      "",         // "" or null = no pause — treat both as no pause
                                   // active values: "MANUAL" | "MAX_LOSS_BREACHED" | "MAX_PROFIT_REACHED"
    paper_keys_linked: true,       // derived by backend — raw keys never returned
    live_keys_linked:  false,
    max_contracts:     2,
    max_daily_loss:    -500,       // negative number
    max_daily_profit:  1000,       // positive number
};


// ─────────────────────────────────────────────────────────────────────────────
// STATUS  —  mirrors GET /follower/status response
// Shape: { status, daily_pnl, open: [...], closed: [...] }
// servantPositionEvent patches this state — do not re-fetch REST on each event
// ─────────────────────────────────────────────────────────────────────────────
export const mockStatus = {
    status:    "OK",
    daily_pnl: -44.00,   // float, already dollar value — resets each trading day

    // Open positions
    // Pending fill inference: entry_fill_price === 0 && status === "open"  → show "Awaiting fill"
    // No "pending_fill" status exists in the backend
    open: [
        {
            master_position_id: "pos_abc123",
            contract_symbol:    "SPY250422C00570000",
            option_type:        "call",      // always lowercase: "call" | "put"
            qty:                2,
            entry_fill_price:   1.45,        // 0 = fill not yet confirmed → "Awaiting fill"
            underlying_entry:   570.20,
            underlying_target:  571.10,
            underlying_stop:    569.80,
            status:             "open",      // "open" | "closed" | "failed"
            dt:                 "2026-04-22",
            open_at:            "2026-04-22T10:14:32Z",
            exit_fill_price:    0,           // 0 (not null) for open positions
            realized_pnl:       0,           // 0 (not null) for open positions
            closed_at:          "",          // "" (not null) for open positions
        },
        {
            // Second open position — pending fill (entry_fill_price === 0)
            master_position_id: "pos_pending001",
            contract_symbol:    "SPY250422C00572000",
            option_type:        "call",
            qty:                1,
            entry_fill_price:   0,           // fill not yet confirmed
            underlying_entry:   571.80,
            underlying_target:  572.90,
            underlying_stop:    571.10,
            status:             "open",
            dt:                 "2026-04-22",
            open_at:            "2026-04-22T11:02:10Z",
            exit_fill_price:    0,
            realized_pnl:       0,
            closed_at:          "",
        }
    ],

    // Closed positions — today only for MVP
    // realized_pnl is already dollar value: (exit_fill - entry_fill) * qty * 100
    closed: [
        {
            master_position_id: "pos_xyz789",
            contract_symbol:    "SPY250422P00568000",
            option_type:        "put",
            qty:                1,
            entry_fill_price:   0.98,
            exit_fill_price:    1.42,
            underlying_entry:   568.40,
            underlying_target:  567.50,
            underlying_stop:    569.00,
            status:             "closed",
            dt:                 "2026-04-22",
            open_at:            "2026-04-22T09:45:10Z",
            closed_at:          "2026-04-22T09:56:48Z",
            realized_pnl:       44.00,
        },
        {
            master_position_id: "pos_def456",
            contract_symbol:    "SPY250422C00569000",
            option_type:        "call",
            qty:                2,
            entry_fill_price:   1.10,
            exit_fill_price:    0.66,
            underlying_entry:   569.20,
            underlying_target:  570.50,
            underlying_stop:    568.50,
            status:             "closed",
            dt:                 "2026-04-22",
            open_at:            "2026-04-22T09:58:00Z",
            closed_at:          "2026-04-22T10:01:22Z",
            realized_pnl:       -88.00,
        }
    ]
};


// ─────────────────────────────────────────────────────────────────────────────
// EXPLANATIONS  —  built from servantExplanationEvent
// Two separate events per trade: type "entry" then type "exit"
// Keyed by master_position_id in local state
//
// Explanation string format:
//   "Headline\n  - Detail line 1\n  - Detail line 2"
//   Split on "\n" → index 0 = headline, rest = bullet lines
// ─────────────────────────────────────────────────────────────────────────────
export const mockExplanations = {
    "pos_abc123": {
        entry_explanation: "Bullish continuation setup\n  - Direction aligned with bullish trend\n  - Risk conditions passed\n  - Acceptable reward-to-risk (confidence: 82%)",
        exit_explanation:  null,        // null until exit event arrives
        close_reason:      "",          // "target_hit"|"stop_loss"|"no_expansion_timeout"|"stock_closed"
        reactive_signal:   "",          // "no_expansion"|"flip_rate_high"|"structure_failure"|"opposing_pressure"|""
    },
    "pos_xyz789": {
        entry_explanation: "Failed breakout reversal on PUT side\n  - Resistance at 569 held\n  - Momentum reversal confirmed\n  - Structure failure flagged by model",
        exit_explanation:  "Trade closed at target\n  - Underlying exit: 567.50\n  - Full target expansion achieved",
        close_reason:      "target_hit",
        reactive_signal:   "",
    },
    "pos_def456": {
        entry_explanation: "Bullish breakout attempt at 569\n  - Volume above average\n  - Confidence: 74%",
        exit_explanation:  "Trade closed at stop loss\n  - Underlying failed to expand\n  - No-expansion timeout triggered",
        close_reason:      "no_expansion_timeout",
        reactive_signal:   "no_expansion",
    },
};


// ─────────────────────────────────────────────────────────────────────────────
// SOCKET EVENT ENVELOPES
// ALL 4 servant events arrive wrapped — data field is a JSON STRING
// Always: const payload = JSON.parse(event.data)
// market_status "SERVANT_LIMIT" is exclusive to servantLimitEvent
// ─────────────────────────────────────────────────────────────────────────────

// servantPositionEvent
export const mockPositionEvent = {
    data: JSON.stringify({
        master_position_id: "pos_abc123",
        contract_symbol:    "SPY250422C00570000",
        option_type:        "call",
        qty:                2,
        entry_fill_price:   1.45,
        underlying_entry:   570.20,
        underlying_target:  571.10,
        underlying_stop:    569.80,
        status:             "open",
        dt:                 "2026-04-22",
        open_at:            "2026-04-22T10:14:32Z",
        exit_fill_price:    0,
        realized_pnl:       0,
        closed_at:          "",
    }),
    ts:            "2026-04-22 10:14:32",  // EST formatted
    market_status: "SERVANT",
};

// servantExplanationEvent — entry
export const mockExplanationEntryEvent = {
    data: JSON.stringify({
        master_position_id: "pos_abc123",
        type:               "entry",
        explanation:        "Bullish continuation setup\n  - Direction aligned with bullish trend\n  - Risk conditions passed\n  - Acceptable reward-to-risk (confidence: 82%)",
    }),
    ts:            "2026-04-22 10:14:35",
    market_status: "SERVANT",
};

// servantExplanationEvent — exit
export const mockExplanationExitEvent = {
    data: JSON.stringify({
        master_position_id: "pos_abc123",
        type:               "exit",
        explanation:        "Trade closed at target\n  - Underlying exit: 571.05",
        close_reason:       "target_hit",
        reactive_signal:    "",
    }),
    ts:            "2026-04-22 10:16:10",
    market_status: "SERVANT",
};

// servantLimitEvent — note: market_status is "SERVANT_LIMIT" for this event
export const mockLimitEvent = {
    data: JSON.stringify({
        type:      "MAX_LOSS_BREACHED",   // | "MAX_PROFIT_REACHED"
        daily_pnl: -512.00,
        limit:     -500,
        message:   "Daily limit reached. No further trades today.",  // exact backend string
    }),
    ts:            "2026-04-22 11:30:00",
    market_status: "SERVANT_LIMIT",
};

// servantOrderEvent
// Stale orders (unfilled > 2 min) arrive with status "stale" + age_seconds field
export const mockOrderEvent = {
    data: JSON.stringify({
        master_position_id: "pos_abc123",
        contract_symbol:    "SPY250422C00570000",
        order_id:           "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
        side:               "buy",       // "buy" | "sell"
        qty:                2,
        status:             "pending",   // "pending"|"filled"|"cancelled"|"stale"|"failed"
        // age_seconds: 135              // present only when status === "stale"
    }),
    ts:            "2026-04-22 10:14:30",
    market_status: "SERVANT",
};


// ─────────────────────────────────────────────────────────────────────────────
// SOCKET HANDLER PATTERN  —  copy this pattern for all 4 events
// ─────────────────────────────────────────────────────────────────────────────
//
//  socket.on('servantPositionEvent', (event) => {
//    const payload = JSON.parse(event.data)
//    // use payload fields
//  })
//
//  Explanation state merge pattern:
//  socket.on('servantExplanationEvent', (event) => {
//    const payload = JSON.parse(event.data)
//    setExplanations(prev => ({
//      ...prev,
//      [payload.master_position_id]: {
//        ...prev[payload.master_position_id],
//        [`${payload.type}_explanation`]: payload.explanation,
//        close_reason:    payload.close_reason    ?? "",
//        reactive_signal: payload.reactive_signal ?? "",
//      }
//    }))
//  })
//
// ─────────────────────────────────────────────────────────────────────────────
// RULES — read before building any component
// ─────────────────────────────────────────────────────────────────────────────
//
//  pause_reason:     !val || val === ""  →  no pause (never strict === null)
//  pending fill:     entry_fill_price === 0 && status === "open"  →  "Awaiting fill"
//  open zeros:       exit_fill_price / realized_pnl / closed_at are 0/"" not null
//  PUT prefs:        returns { status: "OK" } — merge sent payload into local state
//  explanation text: split("\n") → [0] headline, [1..] bullet lines
//  username:         never in any socket payload — implicit from JWT + socket room
//
// ─────────────────────────────────────────────────────────────────────────────